import ListItemLoading from "@/components/loading/listItemLoading/listItemLoading";
import { CustomerGroupResponse } from "@/models/response/customerGroup";
import BottomSheet from "@/modules/smsLibrary/BottomSheet";
import CreateGroupSheet from "@/modules/smsLibrary/CreateGroupSheet";
import GroupCard from "@/modules/smsLibrary/GroupCard";
import PageHeader from "@/modules/smsLibrary/PageHeader";
import { GROUPS_PATH, closeSheet, membersPath, useSendToGroup } from "@/modules/smsLibrary/groups";
import classes from "@/modules/smsLibrary/sms.module.scss";
import useRemoveCustomerGroup from "@/services/customerGroup/hooks/useRemoveCustomerGroup";
import { Button, Center, Loader, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconPlus, IconSearch, IconTrash, IconUsers } from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useGetCustomerGroups from "../hooks/useGetCustomerGroups";

const MENU_SHEET = "groupActions";

function CustomerGroups() {
  const navigate = useNavigate();
  const sendToGroup = useSendToGroup();
  const [params, setParams] = useSearchParams();
  const [term, setTerm] = useState("");
  const [search] = useDebouncedValue(term.trim(), 400);
  const [creating, setCreating] = useState(false);
  const [menuGroup, setMenuGroup] = useState<CustomerGroupResponse>();
  const [confirming, setConfirming] = useState(false);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetCustomerGroups({ params: { Count: 10, Term: search } });
  const { mutate: removeGroup, isLoading: removing } = useRemoveCustomerGroup();
  const groups = data?.pages.flatMap((page) => page.result ?? []) ?? [];

  const observer = useRef<IntersectionObserver | null>(null);
  const lastRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isFetchingNextPage) return;
      observer.current?.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) fetchNextPage();
      });
      if (node) observer.current.observe(node);
    },
    [isFetchingNextPage, fetchNextPage, hasNextPage],
  );

  const openMembers = (group: CustomerGroupResponse, openAdd = false) =>
    navigate(membersPath(group.id, openAdd), { state: { group } });

  // /customerGroup/add redirects here with ?create=1. Opened after mount: the sheet closes itself on mount.
  useEffect(() => {
    if (params.get("create") !== "1") return;
    setParams({}, { replace: true });
    setCreating(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmRemove = () =>
    removeGroup(
      { id: menuGroup?.id },
      { onSuccess: () => closeSheet(MENU_SHEET, () => setMenuGroup(undefined)) },
    );

  return (
    <div className={classes.page}>
      <PageHeader title="گروه‌های پیامکی" />
      <div className={classes.body}>
        <p className={classes.intro}>مشتریان را دسته‌بندی کنید تا به هر دسته پیامک جدا بفرستید؛ مثلاً مشتریان ویژه یا عروس‌های این فصل.</p>

        {(groups.length > 0 || term) && (
          <TextInput
            aria-label="جستجوی گروه"
            placeholder="جستجوی نام گروه"
            size="md"
            radius={14}
            leftSection={<IconSearch size={19} stroke={1.8} />}
            value={term}
            onChange={(event) => setTerm(event.currentTarget.value)}
          />
        )}

        {isLoading ? (
          <ListItemLoading count={3} />
        ) : groups.length === 0 ? (
          <div className={classes.empty}>
            {search ? (
              "گروهی با این نام پیدا نشد."
            ) : (
              <>
                <strong>هنوز گروهی نساخته‌اید</strong>
                با گروه‌ها می‌توانید به دسته‌ای از مشتریان، مثل مشتریان ویژه، با یک لمس پیامک بفرستید.
              </>
            )}
          </div>
        ) : (
          groups.map((group, index) => (
            <div key={group.id} ref={index === groups.length - 1 ? lastRef : undefined}>
              <GroupCard
                group={group}
                onOpen={() => openMembers(group)}
                onSend={() => sendToGroup(group)}
                onAdd={() => openMembers(group, true)}
                onMenu={() => {
                  setConfirming(false);
                  setMenuGroup(group);
                }}
              />
            </div>
          ))
        )}
        {isFetchingNextPage && (
          <Center>
            <Loader type="dots" />
          </Center>
        )}
      </div>

      <div className={classes.stickyBar}>
        <Button fullWidth h={52} radius={14} color="myColor.7" fz={15} fw={800} leftSection={<IconPlus size={20} stroke={2.2} />} onClick={() => setCreating(true)}>
          گروه جدید
        </Button>
      </div>

      <CreateGroupSheet
        opened={creating}
        onClose={() => setCreating(false)}
        onCreated={(group) => {
          setCreating(false);
          // Replace the sheet's history entry so back returns to this list.
          if (group?.id) navigate(membersPath(group.id, true), { replace: true, state: { group } });
          else navigate(GROUPS_PATH, { replace: true });
        }}
      />

      <BottomSheet opened={!!menuGroup} onClose={() => setMenuGroup(undefined)} name={MENU_SHEET} title={menuGroup?.name}>
        {confirming ? (
          <div className={classes.noticeText} style={{ gap: 16 }}>
            <span style={{ fontSize: 14, lineHeight: 1.9 }}>گروه «{menuGroup?.name}» حذف شود؟ مشتریان حذف نمی‌شوند، فقط این گروه.</span>
            <div className={classes.summaryActions}>
              <Button flex={1} h={48} radius={12} color="red" loading={removing} onClick={confirmRemove}>
                حذف گروه
              </Button>
              <Button flex={1} h={48} radius={12} variant="default" onClick={() => setConfirming(false)}>
                انصراف
              </Button>
            </div>
          </div>
        ) : (
          <div className={classes.sheetActions}>
            <button
              type="button"
              className={classes.sheetAction}
              onClick={() => {
                const group = menuGroup!;
                setMenuGroup(undefined);
                navigate(membersPath(group.id), { replace: true, state: { group } });
              }}
            >
              <IconUsers size={21} stroke={1.8} aria-hidden />
              مشاهده‌ی اعضا
            </button>
            <button type="button" className={classes.sheetAction} data-danger="true" onClick={() => setConfirming(true)}>
              <IconTrash size={21} stroke={1.8} aria-hidden />
              حذف گروه
            </button>
          </div>
        )}
      </BottomSheet>
    </div>
  );
}

export default CustomerGroups;
